import { AnalyticsDateShortcut } from "../../types/analytics/AnalyticsSavedFilter";
import { IDateRange } from "../../types/generic";
import { dateRangeForShortcut } from "../../config/analytics/StaticDateRanges";
import { sameDateRange } from "./analyticsFilterBarHelpers";

export type AnalyticsDateMode = "fit" | "common" | "pick";

const COMMON_SHORTCUTS = new Set<AnalyticsDateShortcut>([
  "LAST_30_DAYS",
  "LAST_3_MONTHS",
  "LAST_6_MONTHS",
  "LAST_YEAR",
  "LAST_12_MONTHS",
  "YEAR_TO_DATE",
  "LAST_FULL_YEAR",
  "ALL_TIME",
]);

export function isCommonDateShortcut(
  shortcut: AnalyticsDateShortcut,
): boolean {
  return COMMON_SHORTCUTS.has(shortcut);
}

/** Map a staged date shortcut to the mode tab that represents it. */
export function dateModeForShortcut(
  shortcut: AnalyticsDateShortcut,
): AnalyticsDateMode {
  if (shortcut === "FIT_TO_JOURNEYS") return "fit";
  if (isCommonDateShortcut(shortcut)) return "common";
  return "pick";
}

export type ActivityWindowWriteTarget = "staged-only" | "staged-and-applied";

/**
 * Where a resolved All-activity window is allowed to land. Applied is only safe
 * when nothing the window was resolved from is staged-but-unapplied: a window
 * resolved for a staged journey selection must not reach applied, or charts
 * requery on the journey popover's Done, before the band Apply.
 */
export function activityWindowWriteTarget(args: {
  /** Dates Done switched into All activity — stage only so it dirties until Apply. */
  stagesOnlyRequested: boolean;
  journeyDirty: boolean;
  datesDirty: boolean;
  hasAppliedDateRange: boolean;
}): ActivityWindowWriteTarget {
  const { stagesOnlyRequested, journeyDirty, datesDirty, hasAppliedDateRange } =
    args;
  if (stagesOnlyRequested || journeyDirty) return "staged-only";
  // First paint has no applied window yet, so the resolve bootstraps both.
  if (!hasAppliedDateRange) return "staged-and-applied";
  return datesDirty ? "staged-only" : "staged-and-applied";
}

/**
 * Resolve popover/panel date chrome into a staged shortcut (+ optional range).
 * Does not write React state — callers stage or apply the result.
 */
export type DateChromeResolveResult =
  | {
      ok: true;
      shortcut: AnalyticsDateShortcut;
      /** Absolute range to stage; null for All activity (FIT resolve fills it). */
      range: IDateRange | null;
      changed: boolean;
      /**
       * When true, the next FIT activity-window resolve should write staged dates
       * only (not applied), so Done → All activity dirties until band Apply.
       */
      fitResolveStagesOnly: boolean;
    }
  | { ok: false };

export function resolveDateChromeIntent(args: {
  dateMode: AnalyticsDateMode;
  stagedShortcut: AnalyticsDateShortcut;
  stagedRange: IDateRange | null | undefined;
  exactRange: IDateRange | null;
  commonDraftPreset: AnalyticsDateShortcut | null;
}): DateChromeResolveResult {
  const { dateMode, stagedShortcut, stagedRange, exactRange, commonDraftPreset } =
    args;

  if (dateMode === "pick") {
    if (!exactRange) return { ok: false };
    const unchanged =
      stagedShortcut === "CUSTOM" &&
      sameDateRange(exactRange, stagedRange ?? null);
    return {
      ok: true,
      shortcut: "CUSTOM",
      range: exactRange,
      changed: !unchanged,
      fitResolveStagesOnly: false,
    };
  }

  if (dateMode === "fit") {
    const changed = stagedShortcut !== "FIT_TO_JOURNEYS";
    return {
      ok: true,
      shortcut: "FIT_TO_JOURNEYS",
      range: null,
      changed,
      fitResolveStagesOnly: changed,
    };
  }

  if (!commonDraftPreset) return { ok: false };
  const range = dateRangeForShortcut(commonDraftPreset);
  if (!range) return { ok: false };
  const changed = commonDraftPreset !== stagedShortcut;
  return {
    ok: true,
    shortcut: commonDraftPreset,
    range: changed ? range : null,
    changed,
    fitResolveStagesOnly: false,
  };
}
